import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import Button from "components/ui/Button";
import Text from "components/ui/Text";
import { fetchLatestBuildVersion, refreshWebapp } from "services/buildVersion";
import { useUser } from "contexts/UserContext";
import styles from "./useNewVersionToast.module.scss";

const FETCH_VERSION_PERIOD = 60 * 1000;

// Seeded from the build-time constant so that a deploy landing before the first
// poll still counts as new: a runtime-seeded version would record it as this
// tab's own, never prompting while the tab runs a no-longer-deployed bundle.
const runningVersion = __BUILD_VERSION__;

// Dismissal is remembered per deployed version: the prompt returns for the next
// deploy, not on the next poll of the one just dismissed.
let notifiedVersion: string | null = null;

const NEW_VERSION_TOAST_ID = "new-version";

// Poll the deployed version and prompt for a refresh when this tab falls behind.
export default function useNewVersionToast() {
  const user = useUser(false);
  const isLoggedIn = !!user;

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        const latestVersion = await fetchLatestBuildVersion();
        if (latestVersion !== runningVersion && latestVersion !== notifiedVersion) {
          notifiedVersion = latestVersion;
          toast(({ closeToast }) => <NewVersionMessage closeToast={closeToast} />, {
            toastId: NEW_VERSION_TOAST_ID,
            icon: <AutorenewIcon fontSize="large" htmlColor="var(--action-ink)" />, // --action-ink
            className: styles.versionToast,
            autoClose: false,
            closeOnClick: false,
            closeButton: false,
          });
        }
      } catch {
        // fetchLatestBuildVersion already logs; swallow so a transient network
        // blip does not raise an unhandled rejection once a minute.
      }
    }, FETCH_VERSION_PERIOD);

    return () => clearInterval(intervalId);
  }, [isLoggedIn]);
}

function NewVersionMessage({ closeToast }: { closeToast: () => void }) {
  const { t } = useTranslation();

  return (
    <div className={styles.message}>
      <Text variant="body-3" className={styles.copy} align="center">
        {t("Common.newVersionToast.message")}
      </Text>
      <div className={styles.actions}>
        <Button size="small" variant="secondary" onClick={closeToast}>
          {t("Common.actions.later")}
        </Button>
        <Button size="small" onClick={refreshWebapp}>
          {t("Common.actions.refreshNow")}
        </Button>
      </div>
    </div>
  );
}
